/**
 * The citation chain: the sequence of citations the reader has opened.
 *
 * The audit trail no longer lists every piece of evidence up front. A reader
 * moving through an answer clicks the phrases they want to check, and each
 * click appends that citation's card to the trail — so the panel ends up
 * holding the reader's own path through the sources rather than a dump of
 * everything the model touched.
 *
 * Three rules the surface depends on, all enforced here rather than at the
 * call sites:
 *
 * - **Append in click order.** The chain is a record of what was checked and
 *   when; re-sorting it (by sequence, by kind) would throw that away.
 * - **Re-clicking is a revisit, not a duplicate.** Clicking a phrase whose
 *   card is already open bumps a revision counter instead of adding a second
 *   copy, which the panel uses to scroll to and flash the existing card.
 * - **The chain belongs to one message.** Reading a different answer starts a
 *   different chain; a stale card under a new answer would be a citation for
 *   something the reader is no longer looking at.
 */

import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import type { EvidenceEvent } from "../../types/evidence";
import type { ParsedDopplerUri } from "../../utils/dopplerUri";

export interface CitationChainEntry {
	/** Stable identity — the evidence event id, or the anchor's own key. */
	key: string;
	/** The evidence event, when the anchor bound to one. */
	event: EvidenceEvent | null;
	/** The parsed anchor, so an unbound citation can still name its target. */
	target: ParsedDopplerUri;
	/**
	 * Bumped each time an already-open citation is clicked again. The panel
	 * watches it to re-scroll and re-flash; the entry itself never moves.
	 */
	revision: number;
}

export interface CitationChainValue {
	chain: CitationChainEntry[];
	/** Append, or bump the revision when already present. */
	open: (entry: Omit<CitationChainEntry, "revision">) => void;
	dismiss: (key: string) => void;
	clear: () => void;
	/** The most recently opened or re-clicked key, for scroll/flash. */
	focusedKey: string | null;
}

const CitationChainContext = createContext<CitationChainValue | null>(null);

export interface CitationChainProviderProps {
	/**
	 * Identity of the answer being read. When it changes the chain resets —
	 * see the third rule above. `null` is a valid identity (no message
	 * selected) and also resets.
	 */
	messageKey?: string | null;
	/**
	 * When false the provider is inert — `useCitationChain` sees no context, so
	 * inline citations keep their pre-chain navigate-on-click behaviour and the
	 * audit panel keeps its standalone evidence list. The chain belongs to the
	 * audit-timeline demo surface and rides its flag
	 * (`REACT_APP_AUDIT_TIMELINE_ENABLED`) rather than introducing a second one
	 * that could disagree with it.
	 */
	enabled?: boolean;
	children: React.ReactNode;
}

export const CitationChainProvider: React.FC<CitationChainProviderProps> = ({
	messageKey = null,
	enabled = true,
	children,
}) => {
	const [chain, setChain] = useState<CitationChainEntry[]>([]);
	const [focusedKey, setFocusedKey] = useState<string | null>(null);
	// Tracked in a ref as well as state so the reset effect can skip the first
	// render — mounting is not a message change, and clearing an empty chain
	// would be harmless but the extra render is not free on a long thread.
	const lastMessageKey = useRef<string | null | undefined>(undefined);

	useEffect(() => {
		if (lastMessageKey.current === undefined) {
			lastMessageKey.current = messageKey;
			return;
		}
		if (lastMessageKey.current === messageKey) return;
		lastMessageKey.current = messageKey;
		setChain([]);
		setFocusedKey(null);
	}, [messageKey]);

	const open = useCallback((entry: Omit<CitationChainEntry, "revision">) => {
		setChain((current) => {
			const index = current.findIndex((item) => item.key === entry.key);
			if (index === -1) return [...current, { ...entry, revision: 0 }];
			// Revisit: same position, newer revision, and refreshed payload in
			// case the event arrived after the first click (live streaming).
			const next = [...current];
			next[index] = {
				...entry,
				revision: current[index].revision + 1,
			};
			return next;
		});
		setFocusedKey(entry.key);
	}, []);

	const dismiss = useCallback((key: string) => {
		setChain((current) => current.filter((item) => item.key !== key));
		setFocusedKey((current) => (current === key ? null : current));
	}, []);

	const clear = useCallback(() => {
		setChain([]);
		setFocusedKey(null);
	}, []);

	const value = useMemo(
		() => ({ chain, open, dismiss, clear, focusedKey }),
		[chain, open, dismiss, clear, focusedKey],
	);

	if (!enabled) return <>{children}</>;
	return (
		<CitationChainContext.Provider value={value}>
			{children}
		</CitationChainContext.Provider>
	);
};

/**
 * The chain, or `null` where no provider is mounted.
 *
 * Nullable on purpose: the answer renderer is used in surfaces that have no
 * audit panel (exports, the shared-chat view), and there an inline citation
 * must keep its old navigate-on-click behaviour rather than becoming a button
 * that does nothing.
 */
export const useCitationChain = (): CitationChainValue | null =>
	useContext(CitationChainContext);

export default CitationChainProvider;
